import { useMutation, useQueryClient } from 'react-query';

import { MutationFunction, useRequest } from '@frontend/api';

import type { SavedViewDetailsParams, SavedView } from './types';
import { getSavedViewsListQueryKey } from './utils';

export const useDeleteSavedView: MutationFunction<
  null,
  SavedViewDetailsParams,
  []
> = ({ query, requestOptions, queryOptions } = {}) => {
  const request = useRequest(requestOptions);
  const queryClient = useQueryClient();
  const listKey = getSavedViewsListQueryKey();

  return useMutation(
    ({ viewId }) =>
      request(`/display_configs/views/${viewId}`, {
        query,
        init: {
          method: 'DELETE',
        },
      }),
    {
      ...queryOptions,
      onMutate: async (vars) => {
        queryOptions?.onMutate?.(vars);

        await queryClient.cancelQueries(listKey);
        const previousViews = queryClient.getQueryData(listKey);
        queryClient.setQueryData(
          listKey,
          (old?: SavedView[]) => old?.filter((v) => v.id !== vars.viewId) ?? [],
        );

        return { previousViews };
      },
      onError: (...args) => {
        queryOptions?.onError?.(...args);

        const [, , context] = args;
        queryClient.setQueryData(listKey, context?.previousViews ?? []);
      },
      onSettled: (...args) => {
        queryOptions?.onSettled?.(...args);

        queryClient.invalidateQueries({ queryKey: listKey });
      },
      mutationKey: ['deleteSavedView'],
    },
  );
};
